/**
 * @fileoverview Preferences Service - localStorage-backed user preferences
 *
 * Stores user preferences in the browser's localStorage and exposes a stable
 * async interface (theme, dashboard, UI, and page-view preferences) so callers
 * do not depend on the underlying storage mechanism.
 *
 * The previously-stubbed "API backend (future)" path was removed in the
 * dead-call cleanup: no matching backend route exists (the
 * `/orgs/{org}/users/{user}/preferences*` and `.../dashboards*` endpoints are
 * absent from the live route inventory), so the API adapter only ever produced
 * dead requests. Persistence is local-only and never attempts a backend sync.
 *
 * @example
 * // Get theme preferences
 * const theme = await preferencesService.getTheme();
 *
 * // Update theme
 * await preferencesService.setTheme({ themeName: 'orange' });
 */

import type {
  DashboardConfig,
  DashboardUpsert,
  ThemePreferences,
  UIPreferences,
  PageViewPreferences,
} from '@/types/preferences';

/**
 * Storage backend type. Only `localStorage` is implemented; the `api` member is
 * retained so `isApiBackend()` keeps a stable boolean contract for callers that
 * branch on it.
 */
type StorageBackend = 'localStorage' | 'api';

/**
 * Storage adapter interface - abstraction over the storage mechanism.
 * @interface
 */
interface StorageAdapter {
  /**
   * Retrieve a value from storage
   * @template T - The expected type of the stored value
   * @param key - Storage key identifier
   * @returns The stored value or null if not found
   */
  get<T>(key: string): Promise<T | null>;

  /**
   * Store a value
   * @template T - The type of value being stored
   * @param key - Storage key identifier
   * @param value - Value to store (will be JSON serialized)
   */
  set<T>(key: string, value: T): Promise<void>;

  /**
   * Remove a value from storage
   * @param key - Storage key identifier
   */
  remove(key: string): Promise<void>;
}

/**
 * localStorage adapter implementation
 * Handles SSR safety and JSON serialization
 */
const localStorageAdapter: StorageAdapter = {
  async get<T>(key: string): Promise<T | null> {
    // SSR safety: localStorage only available in browser
    if (typeof window === 'undefined') return null;
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      // Silently fail on parse errors (corrupted data)
      return null;
    }
  },

  async set<T>(key: string, value: T): Promise<void> {
    if (typeof window === 'undefined') return;
    localStorage.setItem(key, JSON.stringify(value));
  },

  async remove(key: string): Promise<void> {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(key);
  },
};

/**
 * Current storage backend. localStorage is the only implemented backend.
 */
const STORAGE_BACKEND = 'localStorage' as StorageBackend;

/**
 * Returns the active storage adapter.
 * @internal
 */
function getAdapter(): StorageAdapter {
  return localStorageAdapter;
}

/**
 * Unified preferences service (localStorage-backed)
 *
 * Provides methods for managing all user preferences including:
 * - Theme settings (color scheme, dark/light mode)
 * - Dashboard configurations (widgets, layouts)
 * - UI preferences (sidebar state, locale, etc.)
 * - Page-specific view preferences (sort, filters, view mode)
 *
 * @example
 * // Theme operations
 * const theme = await preferencesService.getTheme();
 * await preferencesService.setTheme({ themeName: 'blue' });
 *
 * // Dashboard operations
 * const dashboard = await preferencesService.getDashboard('main');
 * await preferencesService.saveDashboard({ name: 'My Dashboard', widgets: [], layouts: [] });
 */
export const preferencesService = {
  /**
   * Returns the current storage backend type
   * Useful for debugging and conditional UI logic
   */
  getBackendType(): StorageBackend {
    return STORAGE_BACKEND;
  },

  /**
   * Checks if service is using API backend
   * @returns true if using API storage, false for localStorage (always false today)
   */
  isApiBackend(): boolean {
    return STORAGE_BACKEND === 'api';
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Theme Preferences
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Retrieves current theme preferences
   * @returns Theme settings with defaults applied
   */
  async getTheme(): Promise<ThemePreferences> {
    const adapter = getAdapter();
    const themeName = await adapter.get<string>('fieldwatch-theme');
    const themeMode = await adapter.get<string>('fieldwatch-mode');

    return {
      themeName: (themeName as ThemePreferences['themeName']) || 'emerald',
      themeMode: (themeMode as ThemePreferences['themeMode']) || 'dark',
    };
  },

  /**
   * Updates theme preferences
   * @param theme - Partial theme object (only changed fields required)
   *
   * @example
   * // Change only color scheme
   * await preferencesService.setTheme({ themeName: 'orange' });
   *
   * // Change only mode
   * await preferencesService.setTheme({ themeMode: 'light' });
   */
  async setTheme(theme: Partial<ThemePreferences>): Promise<void> {
    const adapter = getAdapter();

    if (theme.themeName) {
      await adapter.set('fieldwatch-theme', theme.themeName);
    }
    if (theme.themeMode) {
      await adapter.set('fieldwatch-mode', theme.themeMode);
    }
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Dashboard Preferences
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Retrieves the stored dashboard configuration.
   *
   * @param id - Dashboard identifier (defaults to 'main')
   * @returns Dashboard config or null if none is stored
   *
   * Note: localStorage stores a single dashboard in a legacy format; the service
   * converts it to the standard DashboardConfig shape transparently.
   */
  async getDashboard(id: string = 'main'): Promise<DashboardConfig | null> {
    const adapter = getAdapter();

    // Legacy localStorage format - single dashboard stored directly
    const raw = await adapter.get<{
      version: number;
      widgets: DashboardConfig['widgets'];
      layouts: DashboardConfig['layouts'];
    }>('fieldwatch_dashboard_v1');

    if (!raw) return null;

    // Convert to standard DashboardConfig format
    return {
      id,
      name: id === 'main' ? 'Ana Panel' : id,
      widgets: raw.widgets || [],
      layouts: raw.layouts || [],
      isDefault: id === 'main',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  },

  /**
   * Creates or updates the stored dashboard configuration.
   *
   * @param dashboard - Dashboard data to save
   *
   * @example
   * await preferencesService.saveDashboard({
   *   name: 'My Custom Dashboard',
   *   widgets: [...],
   *   layouts: [...],
   * });
   */
  async saveDashboard(dashboard: DashboardUpsert): Promise<void> {
    const adapter = getAdapter();

    // Store in legacy single-dashboard format
    await adapter.set('fieldwatch_dashboard_v1', {
      version: 1,
      widgets: dashboard.widgets,
      layouts: dashboard.layouts,
    });
  },

  /**
   * Deletes the stored dashboard configuration.
   * localStorage holds a single dashboard, so the id is not used.
   */
  async deleteDashboard(_id: string): Promise<void> {
    const adapter = getAdapter();
    await adapter.remove('fieldwatch_dashboard_v1');
  },

  /**
   * Lists all dashboards for the current user.
   * localStorage holds a single dashboard, so this returns 0 or 1 entries.
   * @returns Array of dashboard configurations
   */
  async listDashboards(): Promise<DashboardConfig[]> {
    const main = await this.getDashboard('main');
    return main ? [main] : [];
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // UI Preferences
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Retrieves UI preferences with sensible defaults
   * @returns Complete UI preferences object
   */
  async getUIPreferences(): Promise<UIPreferences> {
    const adapter = getAdapter();
    const prefs = await adapter.get<UIPreferences>('fieldwatch_ui_v1');

    // Merge with defaults to ensure all fields present
    return {
      sidebarCollapsed: prefs?.sidebarCollapsed ?? false,
      mapStyle: prefs?.mapStyle ?? 'satellite',
      defaultView: prefs?.defaultView ?? 'grid',
      locale: prefs?.locale ?? 'tr-TR',
      timezone: prefs?.timezone ?? 'Europe/Istanbul',
      dateFormat: prefs?.dateFormat ?? 'DD.MM.YYYY',
      timeFormat: prefs?.timeFormat ?? '24h',
      compactMode: prefs?.compactMode ?? false,
      animationsEnabled: prefs?.animationsEnabled ?? true,
    };
  },

  /**
   * Updates UI preferences (partial update supported)
   * @param prefs - Partial UI preferences to update
   */
  async setUIPreferences(prefs: Partial<UIPreferences>): Promise<void> {
    const adapter = getAdapter();
    const current = await this.getUIPreferences();

    // Merge with current to preserve unchanged fields
    await adapter.set('fieldwatch_ui_v1', { ...current, ...prefs });
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Page View Preferences
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Retrieves all page-specific view preferences
   * @returns Map of page keys to their view settings
   */
  async getPageViewPreferences(): Promise<PageViewPreferences> {
    const adapter = getAdapter();
    return (await adapter.get<PageViewPreferences>('fieldwatch_page_views_v1')) || {};
  },

  /**
   * Updates view preferences for a specific page
   *
   * @param pageKey - Unique identifier for the page (e.g., 'fields', 'devices')
   * @param prefs - View preferences for that page
   *
   * @example
   * await preferencesService.setPageViewPreference('devices', {
   *   viewMode: 'table',
   *   sortBy: 'name',
   *   sortOrder: 'asc',
   *   pageSize: 50,
   * });
   */
  async setPageViewPreference(
    pageKey: string,
    prefs: PageViewPreferences[string]
  ): Promise<void> {
    const adapter = getAdapter();
    const current = await this.getPageViewPreferences();

    await adapter.set('fieldwatch_page_views_v1', {
      ...current,
      [pageKey]: prefs,
    });
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Bulk Operations
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Retrieves all preferences in a single call
   * Useful for initial app load to reduce number of storage operations
   *
   * @returns Object containing all preference categories
   */
  async getAllPreferences(): Promise<{
    theme: ThemePreferences;
    ui: UIPreferences;
    pageViews: PageViewPreferences;
    dashboards: DashboardConfig[];
  }> {
    // Parallel fetch for better performance
    const [theme, ui, pageViews, dashboards] = await Promise.all([
      this.getTheme(),
      this.getUIPreferences(),
      this.getPageViewPreferences(),
      this.listDashboards(),
    ]);

    return { theme, ui, pageViews, dashboards };
  },
};

export default preferencesService;
